import { useState } from "react";
import api, { errorMessage } from "../lib/api";
import { Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * ShareWhatsAppButton — v1.8.1
 * Props:
 *   - jenis: 'bon_kain' | 'bon_peralatan' | 'setor' | 'kasbon' | 'reject' | 'setor_perbaikan' | 'totalan'
 *   - id: tx id
 *   - vendor_no_hp: raw string (e.g. '0812-3456-7890' or null)
 *   - vendor_nama: string
 *   - nota_kode: nomor nota (e.g. 'BK-2026-0001')
 *   - size: 'sm' | 'md' (default md)
 *   - variant: 'solid' | 'ghost' (default solid)
 *   - testid: optional
 */
export function ShareWhatsAppButton({
    jenis, id, vendor_no_hp, vendor_nama, nota_kode,
    size = "md", variant = "solid", testid,
}) {
    const [loading, setLoading] = useState(false);

    const normalizeHp = (raw) => {
        if (!raw) return "";
        // strip spaces, dashes, dots, parens, plus
        let s = String(raw).replace(/[\s\-.()+]/g, "");
        if (!s) return "";
        // Already 62...
        if (s.startsWith("62")) return s;
        // Local 0812... → 62812...
        if (s.startsWith("0")) return "62" + s.slice(1);
        // Bare 812... → 62812...
        return "62" + s;
    };

    const DEFAULT_TEMPLATE = (
        "Halo {vendor_nama},\n" +
        "Berikut nota {jenis} - {nota_kode}.\n" +
        "Link: {share_url}\n" +
        "\n" +
        "Terima kasih,\n" +
        "{perusahaan}"
    );

    const JENIS_LABEL = {
        bon_kain: "Bon Kain", bon_peralatan: "Bon Peralatan",
        setor: "Setor Barang", kasbon: "Kasbon", reject: "Reject",
        setor_perbaikan: "Setor Perbaikan", totalan: "Totalan",
    };

    const buildMessage = (template, share_url, perusahaan) => (template || DEFAULT_TEMPLATE)
        .replaceAll("{vendor_nama}", vendor_nama || "Vendor")
        .replaceAll("{jenis}", JENIS_LABEL[jenis] || jenis)
        .replaceAll("{nota_kode}", nota_kode || id?.slice(0, 8) || "-")
        .replaceAll("{share_url}", share_url || "")
        .replaceAll("{perusahaan}", perusahaan || "CV. Meranggi Jaya Garment");

    const onClick = async () => {
        const hp = normalizeHp(vendor_no_hp);
        if (!hp) {
            toast.error("Vendor belum punya nomor HP. Isi di master vendor dulu.");
            return;
        }
        if (!id || !jenis) {
            toast.error("Data nota tidak lengkap.");
            return;
        }
        setLoading(true);
        try {
            // v1.8.2: backend returns {token} only — compose share_url client-side
            // via window.location.origin agar public host (ingress) selalu benar.
            const [shareR, pengR] = await Promise.all([
                api.post(`/share/pdf/${jenis}/${id}`),
                api.get("/pengaturan").catch(() => ({ data: {} })),
            ]);
            const token = shareR.data?.token || "";
            if (!token) throw new Error("Gagal membuat share token");
            const share_url = `${window.location.origin}/api/share/${token}`;
            const perusahaan = pengR.data?.nama_perusahaan || "CV. Meranggi Jaya Garment";
            const template = pengR.data?.wa_template || "";
            const msg = buildMessage(template, share_url, perusahaan);
            const url = `https://wa.me/${hp}?text=${encodeURIComponent(msg)}`;
            const w = window.open(url, "_blank", "noopener,noreferrer");
            if (!w) {
                toast.error("Popup diblokir. Izinkan popup lalu coba lagi.");
            } else {
                toast.success(`WhatsApp dibuka untuk ${vendor_nama || "vendor"}`);
            }
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setLoading(false);
        }
    };

    const isSm = size === "sm";
    const WA_GREEN = "#25D366";
    const WA_HOVER = "#1EBE5B";

    if (variant === "ghost") {
        return (
            <button
                type="button"
                onClick={onClick}
                disabled={loading}
                data-testid={testid || "wa-share-btn"}
                title="Kirim via WhatsApp — buka chat dengan link PDF pre-filled"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-medium text-sm hover:bg-[rgba(37,211,102,0.1)] disabled:opacity-50"
                style={{ color: WA_GREEN }}
            >
                {loading ? <Loader2 className={`${isSm ? "w-3.5 h-3.5" : "w-4 h-4"} animate-spin`} />
                    : <MessageCircle className={`${isSm ? "w-3.5 h-3.5" : "w-4 h-4"}`} />}
                Kirim WhatsApp
            </button>
        );
    }

    return (
        <button
            type="button"
            onClick={onClick}
            disabled={loading}
            data-testid={testid || "wa-share-btn"}
            title="Kirim via WhatsApp — buka chat dengan link PDF pre-filled"
            className={`inline-flex items-center gap-1.5 ${isSm ? "px-3 py-1.5 text-xs" : "px-4 py-2 text-sm"} rounded-lg font-medium text-white hover:brightness-110 disabled:opacity-50`}
            style={{ background: WA_GREEN }}
            onMouseOver={(e) => { e.currentTarget.style.background = WA_HOVER; }}
            onMouseOut={(e) => { e.currentTarget.style.background = WA_GREEN; }}
        >
            {loading ? <Loader2 className={`${isSm ? "w-3.5 h-3.5" : "w-4 h-4"} animate-spin`} />
                : <MessageCircle className={`${isSm ? "w-3.5 h-3.5" : "w-4 h-4"}`} />}
            Kirim WhatsApp
        </button>
    );
}

export default ShareWhatsAppButton;
